import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Shield, 
  Lock, 
  Key, 
  Server, 
  Eye, 
  AlertTriangle,
  Users,
  FileText,
  Mail,
  ArrowLeft,
  CheckCircle2
} from 'lucide-react';

const SecurityPage = () => {
  const securitySections = [
    {
      icon: Shield,
      title: 'Our Security Approach',
      content: [
        'LoadSurf AI is designed with a practical, risk-aware security model focused on protecting access, minimizing exposure, and supporting safe integrations across connected services.',
        'We take a defense-in-depth approach and continuously improve our controls as the platform evolves.'
      ]
    },
    {
      icon: Key,
      title: 'Data Access & Authorization',
      content: [
        'LoadSurf AI only accesses third-party accounts explicitly authorized by the user',
        'Account access is used solely to provide requested functionality',
        'We do not access or interact with third-party systems beyond the scope required to operate the Services',
        'Users remain responsible for complying with third-party platform terms',
        'When multiple connected products use the same email address, accounts may be automatically linked to enable integrated workflows, as described in our Privacy Policy.'
      ],
      isList: true
    },
    {
      icon: Lock,
      title: 'Credential Handling',
      content: [
        'Credentials and authorization tokens are handled securely',
        'Sensitive authentication data is never shared publicly or exposed to other users',
        'Access to credentials is restricted to systems and services that require it to function',
        'We do not sell or monetize credential data'
      ],
      isList: true
    },
    {
      icon: Server,
      title: 'Infrastructure & Access Controls',
      content: [
        'Cloud-based infrastructure with restricted access',
        'Role-based access controls limit internal access to production systems',
        'Internal access is granted only on a need-to-know basis',
        'Activity is logged and monitored to detect unusual behavior'
      ],
      isList: true
    },
    {
      icon: Shield,
      title: 'Encryption & Data Protection',
      content: [
        'Data is encrypted in transit where appropriate',
        'Secure communication protocols are used for integrations and APIs',
        'We follow industry best practices to protect stored data',
        'While no system can guarantee absolute security, we take reasonable and appropriate steps to reduce risk.'
      ],
      isList: true
    },
    {
      icon: Eye,
      title: 'Monitoring & Incident Response',
      content: [
        'Systems are monitored for availability, performance, and security events',
        'We investigate potential security incidents promptly',
        'If a material security incident affecting customer data occurs, we will notify affected users as required by law'
      ],
      isList: true
    },
    {
      icon: Server,
      title: 'Third-Party Services',
      content: [
        'LoadSurf AI integrates with third-party platforms and service providers. While we carefully select vendors, each third-party service operates under its own security and privacy practices.',
        'We encourage users to review the terms and policies of any connected services.'
      ]
    },
    {
      icon: FileText,
      title: 'Compliance & Ongoing Improvements',
      content: [
        'LoadSurf AI is built to support growing security and compliance expectations as the platform scales. Our security program evolves alongside customer needs, regulatory requirements, and industry standards.'
      ]
    }
  ];

  const responsibleUseItems = [
    'Protect their account credentials',
    'Use strong passwords and secure email access',
    'Notify us promptly of any suspected unauthorized access'
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/3 right-1/4 w-96 h-96 bg-sky-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-1/3 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-4xl mx-auto px-4 py-16">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="mb-8">
            <Link to="/" className="inline-flex items-center gap-3">
              <img 
                src="/logo/Load Surf SVG.svg" 
                alt="LoadSurf AI" 
                className="h-12 w-12"
              />
              <span className="text-2xl font-bold text-white">
                LoadSurf<span className="text-amber-400">.AI</span>
              </span>
            </Link>
          </div>

          <div className="mb-6">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full">
              <Shield className="w-4 h-4 text-emerald-400" />
              <span className="text-emerald-400 text-sm font-medium">Security</span>
            </span>
          </div>

          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Security at LoadSurf AI
          </h1>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto">
            Built for real-world freight operations. Designed with security in mind.
          </p>
        </div>

        {/* Intro Section */}
        <div className="bg-slate-900/70 border border-slate-800 rounded-3xl p-8 mb-8">
          <p className="text-slate-300 text-lg leading-relaxed">
            LoadSurf AI helps carriers aggregate and manage access to private load boards and connected tools. 
            That means security, data protection, and responsible access are foundational to how we build and operate the platform.
          </p>
        </div>

        {/* Security Sections */}
        <div className="space-y-6 mb-12">
          {securitySections.map((section, index) => (
            <div 
              key={index}
              className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 hover:border-slate-700 transition-colors"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500/20 to-sky-500/20 flex items-center justify-center flex-shrink-0">
                  <section.icon className="w-6 h-6 text-emerald-400" />
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-semibold text-white mb-4">{section.title}</h2>
                  {section.isList ? (
                    <ul className="space-y-3">
                      {section.content.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                          <span className="text-slate-400">{item}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="space-y-3">
                      {section.content.map((paragraph, idx) => (
                        <p key={idx} className="text-slate-400">{paragraph}</p>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Responsible Use Section */}
        <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border border-amber-500/20 rounded-3xl p-8 mb-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center flex-shrink-0">
              <Users className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white mb-2">Responsible Use</h2>
              <p className="text-slate-400 mb-4">Security is a shared responsibility. Users are expected to:</p>
              <ul className="space-y-3">
                {responsibleUseItems.map((item, idx) => (
                  <li key={idx} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-amber-500" />
                    <span className="text-slate-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Learn More Section */}
        <div className="bg-slate-900/70 border border-slate-800 rounded-3xl p-8 mb-12">
          <h2 className="text-xl font-semibold text-white mb-4">Learn More</h2>
          <p className="text-slate-400 mb-6">
            For details on how we handle personal data and account linking, please review:
          </p>
          
          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            <Link 
              to="/privacy"
              className="flex items-center gap-3 p-4 bg-slate-800/50 rounded-xl border border-slate-700 hover:border-sky-500/50 transition-colors group"
            >
              <FileText className="w-5 h-5 text-sky-400" />
              <span className="text-white group-hover:text-sky-400 transition-colors">Privacy Policy</span>
            </Link>
            <Link 
              to="/terms"
              className="flex items-center gap-3 p-4 bg-slate-800/50 rounded-xl border border-slate-700 hover:border-sky-500/50 transition-colors group"
            >
              <FileText className="w-5 h-5 text-sky-400" />
              <span className="text-white group-hover:text-sky-400 transition-colors">Terms & Conditions</span>
            </Link>
          </div>

          <div className="pt-6 border-t border-slate-800">
            <p className="text-slate-400 mb-3">
              For security-related questions or disclosures, contact:
            </p>
            <a 
              href="mailto:info@loadsurf.ai"
              className="inline-flex items-center gap-2 text-sky-400 hover:text-sky-300 transition-colors"
            >
              <Mail className="w-5 h-5" />
              <span>info@loadsurf.ai</span>
            </a>
          </div>
        </div>

        {/* Footer Navigation */}
        <div className="text-center">
          <Link 
            to="/"
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SecurityPage;
